/**
 * Application status helpers.
 *
 * The API never returns raw enum names on read: both the licence mapper
 * (LicenseMapper.toLicenseRequest) and the investment mapper
 * (InvestmentApplication.toDto) push the stored status through
 * UnifiedApprovalDTO.mapToSimpleStatus, so list and detail payloads carry
 * DISPLAY strings such as "Draft", "Under Inspection" or "Application Approved".
 *
 * Filtering, on the other hand, is bound to the LicenseStatus enum
 * (LicenseFilterDTO.status), so the query string has to carry the RAW enum name.
 *
 * Keep the two vocabularies apart: compare against DISPLAY_STATUS, filter with
 * LICENCE_STATUS_OPTIONS.
 */

/** Display strings produced by UnifiedApprovalDTO.mapToSimpleStatus. */
export const DISPLAY_STATUS = {
    DRAFT: "Draft",
    LEGAL_REVIEW: "Legal Review",
    NEEDS_CLARIFICATION: "Needs Clarification",
    REJECTED_BY_LEGAL: "Rejected by Legal",
    LEGAL_APPROVED: "Legal Approved",
    UNDER_INSPECTION: "Under Inspection",
    INSPECTION_FEEDBACK: "Inspection Feedback",
    REJECTED_BY_INSPECTOR: "Rejected by Inspector",
    INSPECTION_PASSED: "Inspection Passed",
    FINAL_REVIEW: "Final Review",
    APPLICATION_REJECTED: "Application Rejected",
    APPLICATION_APPROVED: "Application Approved",
    EXPIRED: "Expired",
    PROCESSING: "Processing",
} as const;

/**
 * Raw LicenseStatus enum name for an application a reviewer has sent back to the
 * applicant to correct and resubmit. It is NOT terminal — unlike REJECTED — and
 * the applicant is the one who has to act on it.
 *
 * UnifiedApprovalDTO.mapToSimpleStatus has no case for this value, so it falls
 * through to the "Processing" default. That means the licence and investment
 * list/detail payloads cannot be told apart from any other unmapped status; the
 * raw name only survives on UnifiedApprovalDTO.currentStatus, i.e. the
 * `/licence/approvals/{id}/approval-status` and `/approvals/{id}/approval-status`
 * responses. Detect it there.
 */
export const RETURNED_FOR_CORRECTION = "RETURNED_FOR_CORRECTION";

/** UI label for it. The API supplies no display string of its own. */
export const RETURNED_FOR_CORRECTION_LABEL = "Returned for Correction";

/** True for the raw enum name, and for the label above once it has been humanised. */
export function isReturnedForCorrection(status?: string | null): boolean {
    if (!status) return false;
    return status.trim().toUpperCase().replace(/[\s-]+/g, "_") === RETURNED_FOR_CORRECTION;
}

/**
 * Text to render for a status. Everything the API maps is already a display
 * string and passes through untouched; RETURNED_FOR_CORRECTION is the one value
 * that can reach the UI as a raw enum name.
 */
export function statusLabel(status?: string | null): string | undefined {
    if (!status || !status.trim()) return undefined;
    return isReturnedForCorrection(status) ? RETURNED_FOR_CORRECTION_LABEL : status;
}

/**
 * "attention" is awaiting-the-applicant, deliberately kept apart from
 * "negative": a returned application is still alive and the customer can act.
 */
export type StatusTone = "neutral" | "progress" | "positive" | "negative" | "attention";

const POSITIVE: string[] = [
    DISPLAY_STATUS.LEGAL_APPROVED,
    DISPLAY_STATUS.INSPECTION_PASSED,
    DISPLAY_STATUS.APPLICATION_APPROVED,
];

const NEGATIVE: string[] = [
    DISPLAY_STATUS.REJECTED_BY_LEGAL,
    DISPLAY_STATUS.REJECTED_BY_INSPECTOR,
    DISPLAY_STATUS.APPLICATION_REJECTED,
    DISPLAY_STATUS.EXPIRED,
];

const PROGRESS: string[] = [
    DISPLAY_STATUS.LEGAL_REVIEW,
    DISPLAY_STATUS.NEEDS_CLARIFICATION,
    DISPLAY_STATUS.UNDER_INSPECTION,
    DISPLAY_STATUS.INSPECTION_FEEDBACK,
    DISPLAY_STATUS.FINAL_REVIEW,
];

export function statusTone(status?: string | null): StatusTone {
    if (!status) return "neutral";

    if (isReturnedForCorrection(status)) return "attention";
    if (POSITIVE.includes(status)) return "positive";
    if (NEGATIVE.includes(status)) return "negative";
    if (PROGRESS.includes(status)) return "progress";

    return "neutral";
}

/** Tone mapped onto the ExtendedBadge `variant` vocabulary. */
export function statusBadgeVariant(
    status?: string | null
): "primary" | "success" | "warning" | "danger" {
    switch (statusTone(status)) {
        case "positive": return "success";
        case "negative": return "danger";
        // Not "danger": the application is not dead, it is waiting on the applicant.
        case "attention": return "warning";
        case "progress": return "warning";
        default: return "primary";
    }
}

/** Tone mapped onto the Badge `intent` vocabulary. */
export function statusBadgeIntent(
    status?: string | null
): "primary" | "info" | "warning" | "error" | "success" | "default" {
    switch (statusTone(status)) {
        case "positive": return "success";
        case "negative": return "error";
        // Its own intent, so a returned application never reads as a rejection.
        case "attention": return "primary";
        case "progress": return "warning";
        default: return "default";
    }
}

/**
 * Human label for a raw ComplaintStatus enum name ("IN_REVIEW" -> "In review").
 * Badge only applies CSS `capitalize`, which leaves SCREAMING_SNAKE_CASE intact.
 */
export function complaintStatusLabel(status?: string | null): string {
    if (!status) return "N/A";
    const words = status.replace(/_/g, " ").toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * ComplaintStatus is the one status the API serialises raw: ComplaintDTO.status
 * is the enum itself and never goes through mapToSimpleStatus.
 */
export function complaintStatusIntent(
    status?: string | null
): "primary" | "info" | "warning" | "error" | "success" | "default" {
    switch (status) {
        case "SUBMITTED":
        case "IN_REVIEW":
        case "IN_PROGRESS":
            return "warning";
        case "RESOLVED":
            return "success";
        case "REJECTED":
            return "error";
        default:
            return "default";
    }
}

/**
 * Raw LicenseStatus enum values, the only thing LicenseFilterDTO.status accepts.
 * Labels are humanised enum names, NOT mapToSimpleStatus output: several enum
 * values collapse onto the same display string, so a display label would not
 * identify the value being filtered on.
 */
export const LICENCE_STATUS_OPTIONS: Array<{ name: string, value: string }> = [
    { name: "Draft", value: "DRAFT" },
    { name: "Submitted", value: "SUBMITTED" },
    { name: "Under Review", value: "UNDER_REVIEW" },
    { name: "Inspection Pending", value: "INSPECTION_PENDING" },
    { name: "Inspection In Progress", value: "INSPECTION_IN_PROGRESS" },
    { name: "Inspection Completed", value: "INSPECTION_COMPLETED" },
    { name: "Inspection Rejected", value: "INSPECTION_REJECTED" },
    { name: "Legal Review Pending", value: "LEGAL_REVIEW_PENDING" },
    { name: "Legal Review Completed", value: "LEGAL_REVIEW_COMPLETED" },
    { name: "Final Review Pending", value: "FINAL_REVIEW_PENDING" },
    { name: "Final Completed", value: "FINAL_COMPLETED" },
    { name: "Approved", value: "APPROVED" },
    { name: "Rejected", value: "REJECTED" },
    { name: "Returned for Correction", value: RETURNED_FOR_CORRECTION },
    { name: "Expired", value: "EXPIRED" },
];

/**
 * Display statuses an application can no longer be edited in.
 *
 * The backing rule is LicenseService.validateLicenseUpdateAllowed, which blocks
 * the RAW statuses APPROVED, REJECTED and EXPIRED with a 409. Read payloads only
 * ever carry the display string, so the check has to be made against
 * mapToSimpleStatus output.
 *
 * FINAL_REVIEW_REJECTED collapses onto the same "Application Rejected" display
 * string as REJECTED and so is treated as non-editable here even though the API
 * would allow it — the payload gives the UI nothing to tell the two apart, and
 * offering an edit that the server might accept is the less useful half of that
 * trade.
 */
export const NON_EDITABLE_STATUS: string[] = [
    DISPLAY_STATUS.APPLICATION_APPROVED,
    DISPLAY_STATUS.APPLICATION_REJECTED,
    DISPLAY_STATUS.EXPIRED,
];

/**
 * Whether an applicant may still edit an application in this status.
 *
 * Everything else is editable, RETURNED_FOR_CORRECTION included — that is the
 * one state the applicant is explicitly being asked to act on. An unrecognised
 * or missing status is treated as editable: the server has the final say and
 * answers with a message the UI shows, which beats hiding the action on a guess.
 */
export function isEditableStatus(status?: string | null): boolean {
    if (!status || !status.trim()) return true;
    return !NON_EDITABLE_STATUS.includes(status.trim());
}
